<template>
  <div class="inventory-in-out-report pa-6">
    <!-- Report Content -->
    <v-card class="mb-6 elevation-2" style="border-radius: 12px">
      <v-card-title
        class="d-flex align-center pa-4"
        style="background-color: #ffffff; border-bottom: 1px solid #e0e0e0"
      >
        <v-icon class="mr-3" size="24" color="primary">mdi-file-chart-outline</v-icon>
        <span class="text-h6 font-weight-medium"
          >Reporte de Movimientos de Inventario</span
        >
        <v-spacer></v-spacer>
        <v-btn
          color="error"
          outlined
          class="text-none font-weight-medium mr-3"
          @click="downloadPDF"
          :loading="loadingPDF"
        >
          <v-icon left size="18">mdi-file-pdf-box</v-icon>
          Descargar PDF
        </v-btn>
        <v-btn
          color="success"
          outlined
          class="text-none font-weight-medium"
          @click="exportToExcel"
        >
          <v-icon left size="18">mdi-file-excel</v-icon>
          Exportar Excel
        </v-btn>
      </v-card-title>

      <!-- Filters Section -->
      <v-card-text class="px-6">
        <v-row class="mb-0 pb-0">
          <v-col cols="12">
            <h3 class="text-subtitle-1 font-weight-medium mb-0" style="color: #616161">
              <v-icon class="mr-2" color="primary" size="20">mdi-filter-variant</v-icon>
              Filtros de Búsqueda
            </h3>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.entryType"
              :items="entryTypes"
              item-text="text"
              item-value="value"
              label="Tipo de Movimiento"
              color="primary"
              item-color="primary"
              outlined
              dense
              hide-details
              background-color="grey lighten-5"
              class="elevation-1"
              clearable
            >
              <template #prepend-inner>
                <v-icon color="primary">mdi-swap-horizontal</v-icon>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="business_selected"
              :items="businessUnits"
              item-text="description_long"
              item-value="id"
              label="Unidad de negocio"
              color="primary"
              item-color="primary"
              outlined
              dense
              hide-details
              background-color="grey lighten-5"
              class="elevation-1"
              required
            >
              <template #prepend-inner>
                <v-icon color="primary">mdi-office-building-outline</v-icon>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="location_selected"
              :items="branches"
              item-text="text"
              item-value="value"
              label="Sucursal"
              color="primary"
              item-color="primary"
              outlined
              dense
              hide-details
              background-color="grey lighten-5"
              class="elevation-1"
              return-object
              required
            >
              <template #prepend-inner>
                <v-icon color="primary">mdi-map-marker-outline</v-icon>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.warehouse"
              :items="parentWarehouses"
              :loading="loadingWarehouses"
              item-text="name"
              item-value="id"
              label="Almacén"
              color="primary"
              item-color="primary"
              outlined
              dense
              hide-details
              background-color="grey lighten-5"
              class="elevation-1"
              return-object
              clearable
              @focus="loadParentWarehouses"
            >
              <template #prepend-inner>
                <v-icon color="primary">mdi-warehouse</v-icon>
              </template>
              <template #item="{ item }">
                <v-list-item-avatar>
                  <v-icon color="primary">mdi-warehouse</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.location_description_long">
                    {{ item.location_description_long }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip
                    x-small
                    :color="item.active ? 'success' : 'grey'"
                    text-color="white"
                  >
                    {{ item.active ? "Activo" : "Inactivo" }}
                  </v-chip>
                </v-list-item-action>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <!-- Espacio reservado para mantener el layout -->
          </v-col>
        </v-row>

        <!-- Date Range Filter -->
        <v-row class="mt-0">
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="rangeSelected"
              :items="rangeTime"
              label="Rango de tiempo"
              color="primary"
              item-color="primary"
              outlined
              dense
              hide-details
              background-color="grey lighten-5"
              class="elevation-1"
              @change="onRangeChange"
            >
              <template #prepend-inner>
                <v-icon color="primary">mdi-calendar-range</v-icon>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col v-show="isCustomRange" cols="12" md="3">
            <v-menu
              v-model="menu"
              :close-on-content-click="false"
              transition="slide-y-transition"
              offset-y
              min-width="auto"
              max-width="290px"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="computedDateFromFormatted"
                  label="Fecha desde"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                  hide-details
                  background-color="grey lighten-5"
                  class="elevation-1"
                  color="primary"
                >
                  <template #prepend-inner>
                    <v-icon color="primary">mdi-calendar-start</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="filters.dateFrom"
                @input="menu = false"
                color="primary"
                header-color="primary"
                show-current
                locale="es"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col v-show="isCustomRange" cols="12" md="3">
            <v-menu
              v-model="menu2"
              :close-on-content-click="false"
              transition="slide-y-transition"
              offset-y
              min-width="auto"
              max-width="290px"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="computedDateToFormatted"
                  label="Fecha hasta"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                  hide-details
                  background-color="grey lighten-5"
                  class="elevation-1"
                  color="primary"
                >
                  <template #prepend-inner>
                    <v-icon color="primary">mdi-calendar-end</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="filters.dateTo"
                @input="menu2 = false"
                color="primary"
                header-color="primary"
                show-current
                locale="es"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="6" class="d-flex align-center">
            <v-btn
              color="primary"
              class="mr-3 text-none font-weight-medium custom-btn"
              @click="applyFilters"
              style="border-radius: 8px"
            >
              <v-icon left size="18">mdi-magnify</v-icon>
              Buscar
            </v-btn>
            <v-btn
              outlined
              color="grey darken-1"
              class="text-none font-weight-medium custom-btn"
              @click="clearFilters"
              style="border-radius: 8px"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              Limpiar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Summary Cards -->
    <v-row class="mb-6">
      <v-col cols="12" md="3">
        <v-card
          class="summary-card elevation-2"
          style="
            border-radius: 12px;
            background-color: #ffffff;
            border-left: 4px solid #4caf50;
          "
        >
          <v-card-text class="text-center pa-4">
            <div class="d-flex justify-center mb-2">
              <v-avatar size="20" color="#e8f5e8">
                <v-icon size="20" color="#4CAF50">mdi-arrow-down-bold</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-medium mb-1" style="color: #4caf50">
              {{ summary.totalEntries }}
            </div>
            <div class="text-body-2" style="color: #757575">Total Entradas</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card
          class="summary-card elevation-2"
          style="
            border-radius: 12px;
            background-color: #ffffff;
            border-left: 4px solid #f44336;
          "
        >
          <v-card-text class="text-center pa-4">
            <div class="d-flex justify-center mb-2">
              <v-avatar size="20" color="#ffebee">
                <v-icon size="20" color="#f44336">mdi-arrow-up-bold</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-medium mb-1" style="color: #f44336">
              {{ summary.totalExits }}
            </div>
            <div class="text-body-2" style="color: #757575">Total Salidas</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card
          class="summary-card elevation-2"
          style="
            border-radius: 12px;
            background-color: #ffffff;
            border-left: 4px solid #2196f3;
          "
        >
          <v-card-text class="text-center pa-4">
            <div class="d-flex justify-center mb-2">
              <v-avatar size="20" color="#e3f2fd">
                <v-icon size="20" color="#2196F3">mdi-package-variant</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-medium mb-1" style="color: #2196f3">
              {{ summary.totalProducts }}
            </div>
            <div class="text-body-2" style="color: #757575">Productos Afectados</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-card class="elevation-2" style="border-radius: 12px">
      <v-card-title
        class="pa-4"
        style="background-color: #ffffff; border-bottom: 1px solid #e0e0e0"
      >
        <v-icon class="mr-3" size="24" color="primary">mdi-table-large</v-icon>
        <span class="text-h6 font-weight-medium" style="color: #424242"
          >Movimientos de Inventario</span
        >
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="movements"
        :loading="loading"
        :items-per-page="itemsPerPage"
        :search="search"
        class="elevation-0"
        style="border-radius: 0 0 12px 12px"
      >
        <template slot="item.id" slot-scope="{ item }">
          <span class="text-caption font-weight-medium" style="color: #666">
            #{{ item.item_id }}
          </span>
        </template>

        <template slot="item.date" slot-scope="{ item }">
          <div>
            <div class="text-body-2 font-weight-medium">
              {{ formatDate(item.date) }}
            </div>
            <div class="text-caption" style="color: #666">
              {{ formatTime(item.date) }}
            </div>
          </div>
        </template>

        <template slot="item.type" slot-scope="{ item }">
          <v-chip
            :color="item.type === 'Entrada' ? 'success' : 'error'"
            small
            class="font-weight-medium"
            style="border-radius: 12px"
          >
            <v-icon left size="16" :color="item.type === 'Entrada' ? 'white' : 'white'">
              {{ item.type === "Entrada" ? "mdi-arrow-down" : "mdi-arrow-up" }}
            </v-icon>
            {{ item.type }}
          </v-chip>
        </template>

        <template slot="item.product" slot-scope="{ item }">
          <div class="text-body-2">
            {{ item.product }}
          </div>
        </template>

        <template slot="item.quantity" slot-scope="{ item }">
          <span
            :class="item.type === 'Entrada' ? 'text-success' : 'text-error'"
            class="font-weight-medium"
          >
            {{ item.type === "Entrada" ? "+" : "-"
            }}{{ parseFloat(item.quantity).toLocaleString() }}
          </span>
        </template>

        <template slot="item.warehouse" slot-scope="{ item }">
          <div class="text-body-2">
            {{ item.warehouse }}
          </div>
        </template>

        <template slot="item.business_unit" slot-scope="{ item }">
          <div class="text-body-2">
            {{ item.business_unit }}
          </div>
        </template>

        <template slot="item.location" slot-scope="{ item }">
          <div class="text-body-2">
            {{ item.location }}
          </div>
        </template>

        <template slot="item.actions" slot-scope="{ item }">
          <v-btn
            icon
            small
            color="primary"
            @click="viewDetails(item)"
            class="custom-action-btn"
          >
            <v-icon size="18">mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "InventoryInOutReport",
  data() {
    return {
      loading: false,
      loadingPDF: false,
      search: "",
      itemsPerPage: 25,

      // Date range variables
      menu: false,
      menu2: false,
      rangeSelected: "4", // Default to "Este mes"
      rangeTime: [
        { text: "Hoy", value: "1" },
        // { text: "Ayer", value: "2" },
        { text: "Esta semana", value: "3" },
        { text: "Este mes", value: "4" },
        { text: "Últimos 3 meses", value: "5" },
        { text: "Este año", value: "6" },
        { text: "Personalizado", value: "7" },
      ],
      filters: {
        business: null,
        location: { catalogue_id: null, id: null },
        warehouse: null,
        dateFrom: moment().startOf("month").format("YYYY-MM-DD"),
        dateTo: moment().endOf("month").format("YYYY-MM-DD"),
        movementType: null,
        product: "",
        entryType: "all",
      },
      // Tipos de movimiento para el filtro
      entryTypes: [
        { text: "Todos", value: "all" },
        { text: "Entrada / Salida", value: "entrada_salida" },
        { text: "Entrada", value: "entrada" },
        { text: "Salida", value: "salida" },
        { text: "Ajuste", value: "ajuste" },
        { text: "Transferencia", value: "transferencia" },
      ],
      // Variables para almacenes
      itemsWarehouse: [],
      // Parent warehouses data
      parentWarehouses: [],
      loadingWarehouses: false,
      movementTypes: ["Entrada", "Salida", "Transferencia", "Ajuste", "Devolución"],
      headers: [
        { text: "ID", value: "id", sortable: true, width: "80px" },
        { text: "Fecha", value: "date", sortable: true, width: "140px" },
        { text: "Tipo", value: "type", sortable: true, width: "100px" },
        { text: "Producto", value: "product", sortable: true },
        { text: "Cantidad", value: "quantity", sortable: true, width: "100px" },
        { text: "Ubicación", value: "warehouse", sortable: true },
        { text: "Unidad de Negocio", value: "business_unit", sortable: true },
        { text: "Sucursal", value: "location", sortable: true },
      ],
      movements: [],
      summary: {
        totalEntries: 175,
        totalExits: 50,
        totalProducts: 3,
      },
    };
  },
  mounted() {
    this.setDateRange(); // Initialize date range
    this.initialize();
  },
  computed: {
    business_selected: {
      get() {
        return this.$store.state.app.filters.business;
      },
      set(value) {
        this.$store.commit("setBusiness", value);
        this.filters.business = value;
      },
    },
    location_selected: {
      get() {
        return this.$store.state.app.filters.location;
      },
      set(value) {
        this.$store.commit("setLocation", value);
        this.filters.location = value;
      },
    },
    businessUnits() {
      return this.$store.state.auth.bussines_units || [];
    },
    branches() {
      let data = Object.assign(
        this.$store.state.auth.locations.filter(
          (element) => element.bus_id == this.business_selected
        ),
        []
      );
      return data;
    },
    warehouses() {
      let data = Object.assign(
        this.itemsWarehouse.filter(
          (element) => element.location_id == this.location_selected.value
        ),
        []
      );
      return data;
    },
    // Date formatting computed properties
    computedDateFromFormatted() {
      return this.filters.dateFrom
        ? moment(this.filters.dateFrom).format("DD/MM/YYYY")
        : "";
    },
    computedDateToFormatted() {
      return this.filters.dateTo ? moment(this.filters.dateTo).format("DD/MM/YYYY") : "";
    },
    isCustomRange() {
      return this.rangeSelected === "7";
    },
  },
  watch: {
    business_selected(newVal) {
      if (newVal) {
        this.location_selected = null;
        this.filters.warehouse = null;
        this.parentWarehouses = [];
        this.movements = [];
        this.summary = {
          totalEntries: 0,
          totalExits: 0,
          totalProducts: 0,
          totalValue: 0,
        };
        if (this.location_selected) {
          this.loadParentWarehouses();
        }
      }
    },
    location_selected(newVal) {
      if (newVal) {
        this.filters.warehouse = null;
        this.parentWarehouses = [];
        this.movements = [];
        this.summary = {
          totalEntries: 0,
          totalExits: 0,
          totalProducts: 0,
          totalValue: 0,
        };
        this.loadParentWarehouses();
      }
    },
  },
  methods: {
    async initialize() {
      try {
        // Cargar almacenes
        const warehouses = await this.api_Get("inventory/allWarehouses");
        this.itemsWarehouse = await warehouses.json();

        // Inicializar filtros con valores del store
        this.filters.business = this.business_selected;
        this.filters.location = this.location_selected;

        // Cargar almacenes padre si hay ubicación seleccionada
        if (this.location_selected && this.location_selected.value) {
          await this.loadParentWarehouses();
        }

        // Cargar datos iniciales solo si hay filtros válidos
        if (this.business_selected && this.location_selected) {
          this.loadData();
        }
      } catch (error) {
        console.error("Error al inicializar:", error);
      }
    },
    async loadData() {
      this.loading = true;
      try {
        await this.loadMovements();
        await this.loadSummary();
      } catch (error) {
        console.error("Error al cargar datos:", error);
        this.$toast.error("Error al cargar los datos");
      } finally {
        this.loading = false;
      }
    },

    async loadMovements() {
      try {
        const filters = this.buildFilters();
        const response = await this.api_Post("inventory/inventory_movements", filters);

        if (response.status === 200) {
          const result = await response.json();

          // Verificar si los datos vienen en una propiedad específica
          if (Array.isArray(result)) {
            this.movements = result;
          } else if (result.data && Array.isArray(result.data)) {
            this.movements = result.data;
          } else if (result.movements && Array.isArray(result.movements)) {
            this.movements = result.movements;
          } else {
            console.warn("Estructura de respuesta inesperada:", result);
            this.movements = [];
          }


          // Si no hay datos, agregar datos de ejemplo para prueba
          if (this.movements.length === 0) {
            this.movements = [
              {
                id: 1,
                date: "2024-01-15T10:30:00Z",
                type: "Entrada",
                product: "Producto Ejemplo 1",
                quantity: 100,
                warehouse: "Almacén Principal",
                business_unit: "Unidad Central",
                location: "Santo Domingo",
                value: 1500.0,
              },
              {
                id: 2,
                date: "2024-01-16T14:20:00Z",
                type: "Salida",
                product: "Producto Ejemplo 2",
                quantity: 50,
                warehouse: "Almacén Secundario",
                business_unit: "Unidad Norte",
                location: "Santiago",
                value: 750.0,
              },
            ];
          }
        } else {
          console.error("Error al cargar movimientos - Status:", response.status);
          this.movements = [];
        }
      } catch (error) {
        console.error("Error en loadMovements:", error);
        this.movements = [];
      }
    },

    async loadSummary() {
      try {
        const filters = this.buildFilters();
        const response = await this.api_Post("inventory/inventory_summary", filters);

        if (response.status === 200) {
          const result = await response.json();
          this.summary = {
            totalEntries: parseInt(result.total_entries) || 0,
            totalExits: parseInt(result.total_exits) || 0,
            totalProducts: parseInt(result.total_products) || 0,
            totalValue: parseFloat(result.total_value) || 0,
          };
        } else {
          console.error("Error al cargar resumen");
        }
      } catch (error) {
        console.error("Error en loadSummary:", error);
      }
    },

    buildFilters() {
      const filters = {
        business_unit_id: this.business_selected,
        location_id: this.location_selected?.value,
        warehouse_id: this.filters.warehouse?.id,
        date_from: this.filters.dateFrom,
        date_to: this.filters.dateTo,
      };

      // Agregar filtro de tipo de movimiento si está seleccionado
      if (this.filters.entryType && this.filters.entryType !== "all") {
        filters.movement_type = this.filters.entryType;
      }

      // Remover valores null/undefined
      Object.keys(filters).forEach((key) => {
        if (filters[key] === null || filters[key] === undefined || filters[key] === "") {
          delete filters[key];
        }
      });

      return filters;
    },

    // Método para cargar almacenes padre
    async loadParentWarehouses() {
      if (this.parentWarehouses.length > 0) return; // Ya están cargados

      this.loadingWarehouses = true;
      try {
        let filters = " WHERE w.parent_warehouse_id IS NULL";
        if (this.business_selected) {
          filters += ` AND w.BUSINESS_UNITS_ID = ${this.business_selected}`;
          if (
            this.location_selected &&
            this.location_selected.value &&
            this.location_selected.value !== 0
          ) {
            filters += ` AND w.LOCATION_ID = ${this.location_selected.value}`;
          }
        }

        const data = { filters };
        const response = await this.api_Post("inventory/warehouse/tree", data);

        if (response.status === 200) {
          const result = await response.json();
          this.parentWarehouses = result.filter(
            (warehouse) => warehouse.parent_warehouse_id === null
          );
        } else {
          console.error("Error al cargar almacenes");
          this.parentWarehouses = [];
        }
      } catch (error) {
        console.error("Error al cargar almacenes padre:", error);
        this.parentWarehouses = [];
      } finally {
        this.loadingWarehouses = false;
      }
    },

    async applyFilters() {
      if (!this.business_selected) {
        this.$toast.warning("Selecciona una unidad de negocio");
        return;
      }

      if (!this.location_selected) {
        this.$toast.warning("Selecciona una sucursal");
        return;
      }
      await this.loadData();
    },
    clearFilters() {
      this.filters = {
        business: null,
        location: { catalogue_id: null, id: null },
        warehouse: null,
        dateFrom: "",
        dateTo: "",
        movementType: null,
        product: "",
        entryType: "all",
      };
      this.business_selected = null;
      this.location_selected = null;
      this.movements = [];
      this.summary = {
        totalEntries: 0,
        totalExits: 0,
        totalProducts: 0,
        totalValue: 0,
      };
      this.applyFilters();
    },

    exportToExcel() {
      if (!this.movements || this.movements.length === 0) {
        this.$toast.warning("No hay datos para exportar");
        return;
      }

      let csv = "ID,Fecha,Tipo,Producto,Cantidad,Almacén,Unidad de Negocio,Ubicación";

      for (let row of this.movements) {
        const id = row.item_id || "";
        const fecha = this.formatDate(row.date);
        const tipo = row.type || "";
        const producto = this.deleteDiacriticos(row.product || "");
        const cantidad = row.quantity || 0;
        const almacen = this.deleteDiacriticos(row.warehouse || "");
        const unidadNegocio = this.deleteDiacriticos(row.business_unit || "");
        const ubicacion = this.deleteDiacriticos(row.location || "");

        csv += `\n${id},${fecha},${tipo},"${producto}",${cantidad},"${almacen}","${unidadNegocio}","${ubicacion}"`;
      }

      const filename = `Movimientos_Inventario_${moment().format("DD-MM-YYYY")}`;

      this.generateCsvFile(filename, csv);
      this.$toast.success("Archivo exportado exitosamente");
    },

    generateCsvFile(filename, csvContent) {
      const element = document.createElement("a");
      const file = new Blob([csvContent], { type: "text/csv" });
      element.href = URL.createObjectURL(file);
      element.download = `${filename}.csv`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    },

    deleteDiacriticos(text) {
      if (!text) return "";
      return text
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/["]/g, "'")
        .replace(/[\n\r]/g, " ");
    },
    // Date range methods
    onRangeChange() {
      if (this.rangeSelected !== "7") {
        this.setDateRange();
      }
    },
    setDateRange() {
      const today = moment();
      switch (this.rangeSelected) {
        case "1": // Hoy
          this.filters.dateFrom = today.format("YYYY-MM-DD");
          this.filters.dateTo = today.format("YYYY-MM-DD");
          break;
        // case "2": // Ayer
        //   const yesterday = today.clone().subtract(1, "day");
        //   this.filters.dateFrom = yesterday.format("YYYY-MM-DD");
        //   this.filters.dateTo = yesterday.format("YYYY-MM-DD");
        //   break;
        case "3": // Esta semana
          this.filters.dateFrom = today.clone().startOf("week").format("YYYY-MM-DD");
          this.filters.dateTo = today.clone().endOf("week").format("YYYY-MM-DD");
          break;
        case "4": // Este mes
          this.filters.dateFrom = today.clone().startOf("month").format("YYYY-MM-DD");
          this.filters.dateTo = today.clone().endOf("month").format("YYYY-MM-DD");
          break;
        case "5": // Últimos 3 meses
          this.filters.dateFrom = today
            .clone()
            .subtract(3, "months")
            .startOf("month")
            .format("YYYY-MM-DD");
          this.filters.dateTo = today.clone().endOf("month").format("YYYY-MM-DD");
          break;
        case "6": // Este año
          this.filters.dateFrom = today.clone().startOf("year").format("YYYY-MM-DD");
          this.filters.dateTo = today.clone().endOf("year").format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    },
    // Date formatting methods
    formatDate(dateString) {
      return moment(dateString).format("DD/MM/YYYY");
    },
    formatTime(dateString) {
      return moment(dateString).format("HH:mm:ss");
    },
    viewDetails(item) {
      // Implementar vista de detalles
    },

    async downloadPDF() {
      this.loadingPDF = true;

      try {
        // Usar la misma lógica que buildFilters para construir el payload
        const params = {
          business_unit_id: this.business_selected,
          location_id: this.location_selected?.value,
          warehouse_id: this.filters.warehouse?.id,
          date_from: this.filters.dateFrom,
          date_to: this.filters.dateTo,
        };

        // Agregar filtro de tipo de movimiento si está seleccionado
        if (this.filters.entryType && this.filters.entryType !== "all") {
          params.movement_type = this.filters.entryType;
        }

        // Remover valores null/undefined como en buildFilters
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === undefined || params[key] === "") {
            delete params[key];
          }
        });


        const response = await this.api_Post("inventory/inventory_movements_pdf", params);

        if (response.status === 200) {
          const blob = await response.blob();
          const pdfUrl = URL.createObjectURL(blob);

          // Abrir el PDF en una nueva pestaña
          window.open(pdfUrl, "_blank");

          this.$toast.success("PDF generado exitosamente");
        } else {
          this.$toast.error("Error al generar el PDF");
        }
      } catch (error) {
        console.error("Error downloading PDF:", error);
        this.$toast.error("Error al descargar el PDF");
      }

      this.loadingPDF = false;
    },
  },
};
</script>

<style scoped>
.inventory-in-out-report {
  background-color: #fafafa;
  min-height: 100vh;
}

.text-success {
  color: #4caf50 !important;
}

.text-error {
  color: #f44336 !important;
}

.custom-select >>> .v-input__control {
  border-radius: 6px !important;
}

.custom-date-field >>> .v-input__control {
  border-radius: 6px !important;
}

.custom-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 6px !important;
  transition: all 0.2s ease !important;
}

.custom-btn:hover {
  transform: translateY(-1px) !important;
}

.summary-card {
  transition: all 0.2s ease !important;
  cursor: pointer;
}

.summary-card:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.custom-action-btn {
  border-radius: 50% !important;
  transition: all 0.2s ease !important;
}

.custom-action-btn:hover {
  transform: scale(1.05) !important;
}

/* Estilos para el selector de almacén */
.warehouse-select-card {
  border-radius: 12px;
  overflow: hidden;
}

.warehouse-select-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 0;
}

.warehouse-title {
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
}

.warehouse-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
}

.warehouse-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-field {
  min-width: 200px;
  max-width: 250px;
}

.warehouse-tree {
  max-height: 350px;
  overflow-y: auto;
}

.warehouse-node {
  padding: 4px 0;
}

.warehouse-name {
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.9rem;
}

.warehouse-details {
  margin-top: 2px;
}

.location-info {
  font-size: 0.75rem;
  color: #6c757d;
}

.empty-warehouse-state {
  padding: 40px 20px;
  text-align: center;
}

.empty-warehouse-content {
  max-width: 300px;
  margin: 0 auto;
}

.empty-warehouse-title {
  color: #6c757d;
  font-size: 1.1rem;
  font-weight: 500;
}

.empty-warehouse-subtitle {
  font-size: 0.9rem;
}

.search-results {
  color: #28a745;
  font-weight: 500;
}

.search-highlight {
  background-color: #fff3cd;
  color: #856404;
  padding: 1px 2px;
  border-radius: 2px;
  font-weight: 500;
}

.pulse-icon {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Scrollbar personalizado para el árbol de almacenes */
.warehouse-tree::-webkit-scrollbar {
  width: 6px;
}

.warehouse-tree::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.warehouse-tree::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.warehouse-tree::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Estilos para el treeview */
:deep(.v-treeview-node__root) {
  padding: 2px 0;
}

:deep(.v-treeview-node__content) {
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

:deep(.v-treeview-node__content:hover) {
  background-color: #f8f9fa;
}

:deep(.v-treeview-node--active > .v-treeview-node__content) {
  background-color: #e3f2fd;
  border-left: 3px solid #2196f3;
}

:deep(.v-treeview-node--selected > .v-treeview-node__content) {
  background-color: #e8f5e8;
  border-left: 3px solid #4caf50;
}

:deep(.v-data-table th) {
  background-color: #f5f5f5 !important;
  font-weight: 500 !important;
  color: #424242 !important;
}

:deep(.v-data-table tbody tr:hover) {
  background-color: #f9f9f9 !important;
}

:deep(.v-tabs-slider) {
  height: 2px !important;
  border-radius: 2px !important;
}

/* Responsive para el selector de almacén */
@media (max-width: 768px) {
  .warehouse-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-field {
    min-width: auto;
    max-width: none;
  }

  .warehouse-select-card {
    margin: 0 -16px;
    border-radius: 0;
  }
}
</style>
